import React, { useEffect } from "react";
import {
  Stack,
  FormControlLabel,
  Checkbox,
  Typography,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Box,
  Chip,
  MenuItem,
  FormHelperText,
  Divider,
  alpha,
} from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import CampaignIcon from "@mui/icons-material/Campaign";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createAnnouncement,
  updateAnnouncement,
} from "../redux/actions/announcementActions";
import { getBranches } from "../redux/actions/branchActions";
import { getRoles } from "../redux/actions/roleActions";
import { getCustomers } from "../redux/actions/customerActions";
import { cleanFormData } from "../helpers/formHelpers";

import { AnnouncementAudience } from "../shared/enums/AnnouncementAudience";
import { AnnouncementResponseDto } from "../shared/dtos/announcement/AnnouncementResponseDto";
import { announcementActions } from "../redux/slices/announcement";

const audienceOptions = [
  { value: AnnouncementAudience.ALL_USERS, label: "All System Users" },
  { value: AnnouncementAudience.ALL_CUSTOMERS, label: "All Customers" },
  { value: AnnouncementAudience.SPECIFIC_BRANCHES, label: "Specific Branches" },
  { value: AnnouncementAudience.SPECIFIC_ROLES, label: "Specific Roles" },
  {
    value: AnnouncementAudience.SPECIFIC_CUSTOMERS,
    label: "Specific Customers",
  },
];

const announcementSchema = Yup.object().shape({
  title: Yup.string()
    .max(255, "Title cannot exceed 255 characters")
    .required("Title is required"),
  content: Yup.string().required("Content is required"),
  audience: Yup.string().required("Audience is required"),
  publishDate: Yup.string().nullable(),
  expiryDate: Yup.string().required("Expiry date is required"),
  sendAsSms: Yup.boolean().required("SMS preference is required"),
  targetBranchIds: Yup.array().when("audience", {
    is: AnnouncementAudience.SPECIFIC_BRANCHES,
    then: () =>
      Yup.array().min(1, "Select at least one branch").required("Required"),
    otherwise: () => Yup.array().nullable(),
  }),
  targetRoleIds: Yup.array().when("audience", {
    is: AnnouncementAudience.SPECIFIC_ROLES,
    then: () =>
      Yup.array().min(1, "Select at least one role").required("Required"),
    otherwise: () => Yup.array().nullable(),
  }),
  targetCustomerIds: Yup.array().when("audience", {
    is: AnnouncementAudience.SPECIFIC_CUSTOMERS,
    then: () =>
      Yup.array().min(1, "Select at least one customer").required("Required"),
    otherwise: () => Yup.array().nullable(),
  }),
});

interface CreateOrUpdateAnnouncementFormProps {
  announcement?: AnnouncementResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdateAnnouncementForm: React.FC<
  CreateOrUpdateAnnouncementFormProps
> = ({
  announcement = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { items: branches } = useAppSelector((state) => state.branch);
  const { items: roles } = useAppSelector((state) => state.role);
  const { items: customers } = useAppSelector((state) => state.customer);

  useEffect(() => {
    if (!branches?.length) dispatch(getBranches());
    if (!roles?.length) dispatch(getRoles());
    if (!customers?.length) dispatch(getCustomers({ size: 1000 }));
  }, [branches?.length, customers?.length, dispatch, roles?.length]);

  const getInitialIds = (
    field: "targetBranches" | "targetRoles" | "targetCustomers",
  ): string[] => {
    if (!announcement) return [];
    const data = announcement[field];
    return Array.isArray(data) ? data.map((item) => item.id) : [];
  };

  const initialValues = {
    title: announcement?.title || "",
    content: announcement?.content || "",
    publishDate: announcement?.publishDate
      ? announcement.publishDate.substring(0, 16)
      : "",
    expiryDate: announcement?.expiryDate
      ? announcement.expiryDate.substring(0, 16)
      : "",
    audience: announcement?.audience || ("" as AnnouncementAudience),
    sendAsSms: false,
    targetBranchIds: getInitialIds("targetBranches"),
    targetRoleIds: getInitialIds("targetRoles"),
    targetCustomerIds: getInitialIds("targetCustomers"),
  };

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return "";
    return dateString.length === 16 ? `${dateString}:00` : dateString;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(announcementActions.setErrorUpdate(null))
            : () => dispatch(announcementActions.setErrorCreate(null))
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={announcementSchema}
        onSubmit={(values) => {
          const cleanedValues = cleanFormData(values);
          cleanedValues.publishDate = formatDateTime(values.publishDate);
          cleanedValues.expiryDate = formatDateTime(values.expiryDate);

          if (isEditing && announcement) {
            dispatch(
              updateAnnouncement({
                id: announcement.id,
                values: cleanedValues,
              }),
            );
          } else {
            dispatch(createAnnouncement(cleanedValues));
          }
        }}
      >
        {({
          dirty,
          isValid,
          values,
          setFieldValue,
          touched,
          errors,
          handleChange,
        }: FormikProps<typeof initialValues>) => (
          <Form>
            <Stack spacing={3}>
              <FormTextField name="title" label="Announcement Title" />
              <FormTextField
                name="content"
                label="Message Content"
                multiline
                rows={4}
              />

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <FormTextField
                  name="publishDate"
                  label="Publish Date & Time"
                  type="datetime-local"
                  slotProps={{ inputLabel: { shrink: true } }}
                  helperText="Empty means immediate publish"
                />
                <FormTextField
                  name="expiryDate"
                  label="Expiry Date & Time"
                  type="datetime-local"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Box>

              <Divider>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "text.secondary",
                    px: 1,
                  }}
                >
                  TARGET AUDIENCE CONFIGURATION
                </Typography>
              </Divider>

              <FormSelectField
                name="audience"
                label="Select Primary Audience Group"
                options={audienceOptions}
              />

              {/* Dynamic Multi-Selects based on Audience */}
              {values.audience.startsWith("SPECIFIC") && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: (theme) =>
                      alpha(theme.palette.background.default, 0.5),
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {values.audience ===
                    AnnouncementAudience.SPECIFIC_BRANCHES && (
                    <FormControl
                      fullWidth
                      error={
                        touched.targetBranchIds &&
                        Boolean(errors.targetBranchIds)
                      }
                    >
                      <InputLabel>Select Target Branches</InputLabel>
                      <Select
                        multiple
                        value={values.targetBranchIds || []}
                        onChange={(e) =>
                          setFieldValue("targetBranchIds", e.target.value)
                        }
                        input={<OutlinedInput label="Select Target Branches" />}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as string[]).map((val) => (
                              <Chip
                                key={val}
                                label={
                                  branches?.find((b) => b.id === val)?.name ||
                                  val
                                }
                                size="small"
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {branches?.map((b) => (
                          <MenuItem key={b.id} value={b.id}>
                            {b.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.targetBranchIds && errors.targetBranchIds && (
                        <FormHelperText>
                          {errors.targetBranchIds as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}

                  {/* ... Similarly typed Roles and Customers selects ... */}
                  {values.audience === AnnouncementAudience.SPECIFIC_ROLES && (
                    <FormControl
                      fullWidth
                      error={
                        touched.targetRoleIds && Boolean(errors.targetRoleIds)
                      }
                    >
                      <InputLabel>Select Target Roles</InputLabel>
                      <Select
                        multiple
                        value={values.targetRoleIds || []}
                        onChange={(e) =>
                          setFieldValue("targetRoleIds", e.target.value)
                        }
                        input={<OutlinedInput label="Select Target Roles" />}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as string[]).map((val) => (
                              <Chip
                                key={val}
                                label={
                                  roles?.find((r) => r.id === val)?.name || val
                                }
                                size="small"
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {roles?.map((r) => (
                          <MenuItem key={r.id} value={r.id}>
                            {r.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {values.audience ===
                    AnnouncementAudience.SPECIFIC_CUSTOMERS && (
                    <FormControl
                      fullWidth
                      error={
                        touched.targetCustomerIds &&
                        Boolean(errors.targetCustomerIds)
                      }
                    >
                      <InputLabel>Select Target Customers</InputLabel>
                      <Select
                        multiple
                        value={values.targetCustomerIds || []}
                        onChange={(e) =>
                          setFieldValue("targetCustomerIds", e.target.value)
                        }
                        input={
                          <OutlinedInput label="Select Target Customers" />
                        }
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as string[]).map((val) => (
                              <Chip
                                key={val}
                                label={
                                  customers?.find((c) => c.id === val)?.name ||
                                  val
                                }
                                size="small"
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {customers?.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.sendAsSms}
                    onChange={handleChange}
                    name="sendAsSms"
                    color="secondary"
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                    Also dispatch via SMS Notification (Billing rates apply)
                  </Typography>
                }
              />

              <SubmitButton
                title={
                  isEditing ? "Update Announcement" : "Publish Announcement"
                }
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={isEditing ? <SaveOutlinedIcon /> : <CampaignIcon />}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
export default CreateOrUpdateAnnouncementForm;
